<script>
import axios from '@/utils/axios';

export default {
  inject: ['setLoading'],
  data() {
    return {
      id: null,
      so_number: '',
      item_groups: [],
      finishItemIds: new Set(), // ids of items whose inventory_type === 'finish'
      rows: [this.emptyRow()],
      errors: {},
    };
  },
  methods: {
    emptyRow() {
    return {
        item_group_id: null,
        item_name: null,
        unique_items: [],
        item: null,
        items_list: [],
        quantity: 1,
        description: '',
    };
    },

    // Called from the sales-order list: this.$refs.storeDispatchItemModal.open(row)
    async open(row) {
      this.id = row.id;
      this.so_number = row.so_number;
      this.errors = {};
      this.rows = [this.emptyRow()];
      await Promise.all([
        this.getItemGroups(),
        this.getFinishItemIds(),
      ]);
      this.$nextTick(() => {
        this.$openModal('store_dispatch_item_modal');
      });
    },

    async getItemGroups() {
      this.setLoading(true);
      try {
        const res = await axios.get('/item-groups');
        this.item_groups = res.data.data || [];
      } catch (error) {
        this.$swalToast(error.message, 'error');
      } finally {
        this.setLoading(false);
      }
    },

    // Server-side filters by inventory_type, so we get back only finish items directly.
    // We just need the set of ids to cross-check against /items/unique-dropdown results
    // (that endpoint doesn't expose inventory_type itself).
    async getFinishItemIds() {
      try {
        const res = await axios.get('/items', { params: { inventory_type: 'finish' } });
        // Handle both paginated ({ data: { data: [...] } }) and flat ({ data: [...] }) shapes
        const list = res.data.data?.data || res.data.data || [];
        this.finishItemIds = new Set(list.map(i => i.id));
      } catch (error) {
        this.finishItemIds = new Set();
        this.$swalToast('Failed to load finish item list', 'error');
      }
    },

    addRow() {
      this.rows.push(this.emptyRow());
    },

    removeRow(index) {
      if (this.rows.length === 1) return;
      this.rows.splice(index, 1);
    },

    
    async onCategorySelect(index, itemGroupId) {
    const row = this.rows[index];
    row.item_group_id = itemGroupId;
    row.item_name = null;
    row.item = null;
    row.unique_items = [];
    row.items_list = [];

    if (!itemGroupId) return;

    try {
        const res = await axios.get('/items/unique-dropdown', {
        params: { item_group_id: itemGroupId, inventory_type: 'finish' }
        });
        row.unique_items = res.data.data || [];
    } catch (error) {
        this.$swalToast('Failed to load items for selected category', 'error');
    }
    },

    // Item selected -> load brand-wise variants for that item name
    async onItemSelect(index, itemName) {
      const row = this.rows[index];
      row.item_name = itemName;
      row.item = null;
      row.items_list = [];

      if (!itemName) return;

      try {
        const res = await axios.get('/items/item-wise-brand', {
          params: { item_name: itemName }
        });
        const data = res.data.data || [];
        // Safety filter here too, in case brand API returns mixed inventory types
        row.items_list = data.filter(i => i.inventory_type === 'finish');
      } catch (error) {
        this.$swalToast('Failed to load brands for selected item', 'error');
      }
    },

    onBrandSelect(index, selectedItem) {
      this.rows[index].item = selectedItem;
    },

    getRowError(index, field) {
      const key = `items.${index}.${field}`;
      return this.errors[key] ? this.errors[key][0] : null;
    },

    // Catches validation errors that aren't tied to a specific row/field,
    // e.g. backend returning { items: ["These item(s) are already assigned..."] }
    generalError() {
      const key = Object.keys(this.errors).find(k => !k.includes('.'));
      return key ? this.errors[key][0] : null;
    },

 async save() {
  this.errors = {};

  const invalid = this.rows.find(r => !r.item?.id);
  if (invalid) {
    this.$swalToast('Please select item (with brand) for all rows', 'warning');
    return;
  }

  const invalidQty = this.rows.find(r => !r.quantity || r.quantity <= 0);
  if (invalidQty) {
    this.$swalToast('Please enter a valid quantity for all rows', 'warning');
    return;
  }

  this.setLoading(true);
  try {
    const payload = {
      items: this.rows.map(r => ({
        item_id: r.item.id,
        quantity: r.quantity,
        description: r.description || '',
      })),
    };

        await axios.post(`/sales-orders/${this.id}/store-dispatch-lists`, payload);

        this.$swalToast('Store dispatch item added successfully!', 'success');
        this.$emit('updated');
        this.$closeModal('store_dispatch_item_modal');
        this.resetForm();
      } catch (error) {
        const backendErrors = error.errors || error.response?.data?.errors;
        if (backendErrors) {
          this.errors = backendErrors;
          // Show whichever message is available so it's never silent
          const firstMsg = Object.values(backendErrors)[0]?.[0];
          this.$swalToast(firstMsg || error.response?.data?.message || 'Please fix the errors below', 'error');
        } else {
          this.$swalToast(error.response?.data?.message || error.message, 'error');
        }
      } finally {
        this.setLoading(false);
      }
    },

    resetForm() {
      this.rows = [this.emptyRow()];
      this.errors = {};
    },
  },
};
</script>

<template>
<div class="modal fade" id="store_dispatch_item_modal" tabindex="-1" aria-hidden="true" data-bs-backdrop="static" data-bs-keyboard="false">
  <div class="modal-dialog modal-lg modal-dialog-centered modal-dialog-scrollable">
    <div class="modal-content" style="border:none;border-radius:16px;overflow:hidden;">

      <!-- Header -->
      <div class="modal-header" style="background:linear-gradient(135deg,#0f172a,#1e3a5f);padding:15px 20px;border:none;">
        <div>
          <h5 style="margin:0;font-size:14px;font-weight:700;color:#fff;">Add Store Dispatch Item</h5>
          <small style="font-size:11px;color:#93c5fd;">{{ so_number }}</small>
        </div>
        <!-- <button type="button" class="btn-close" data-bs-dismiss="modal" style="filter:invert(1);opacity:.7;" @click="resetForm"></button> -->
      </div>

      <!-- Body -->
      <div class="modal-body" style="padding:20px;">

        <div v-if="generalError()" style="background:#fff0f3;border:1px solid #fecdd3;border-radius:8px;padding:10px 14px;margin-bottom:14px;color:#e11d48;font-size:12.5px;display:flex;align-items:center;gap:8px;">
          <i class="ti ti-alert-circle"></i> {{ generalError() }}
        </div>

        <div v-for="(row, index) in rows" :key="index"
          style="border:1.5px solid #e8edf3;border-radius:10px;padding:14px;margin-bottom:12px;position:relative;">

          <div style="position:absolute;top:-9px;left:14px;width:19px;height:19px;background:#3b82f6;color:#fff;border-radius:50%;font-size:9px;font-weight:700;display:flex;align-items:center;justify-content:center;">
            {{ index + 1 }}
          </div>

          <div class="row g-2">

           <!-- CATEGORY -->
            <div class="col-md-3">
              <label class="form-label fw-semibold" style="font-size:11px;">Category <span class="text-danger">*</span></label>
              <vue-select :options="item_groups" label="name" :reduce="g => g.id"
                v-model="row.item_group_id" placeholder="Select Category"
                @update:modelValue="val => onCategorySelect(index, val)" />
            </div>

            <!-- ITEM (finish only) -->
            <div class="col-md-3">
              <label class="form-label fw-semibold" style="font-size:11px;">Item (Finish) <span class="text-danger">*</span></label>
              <vue-select :options="row.unique_items" label="name" v-model="row.item_name"
                :reduce="i => i.name" placeholder="Select Item" :disabled="!row.item_group_id"
                @update:modelValue="val => onItemSelect(index, val)" />
              <div v-if="row.item_group_id && !row.unique_items.length" style="font-size:10px;color:#94a3b8;margin-top:3px;">
                No finish items found in this category
              </div>
            </div>

            <!-- BRAND -->
            <div class="col-md-3">
              <label class="form-label fw-semibold" style="font-size:11px;">Brand <span class="text-danger">*</span></label>
              <vue-select :options="row.items_list"
                :get-option-label="opt => opt.brand?.name || 'N/A'"
                v-model="row.item" placeholder="Select Brand"
                :disabled="!row.item_name"
                @update:modelValue="val => onBrandSelect(index, val)">
                <template v-slot:option="opt">
                  <div style="display:flex;justify-content:space-between;width:100%;">
                    <span>{{ opt.brand?.name || 'N/A' }}</span>
                    <span style="color:#64748b;font-size:11px;">₹{{ opt.price }}</span>
                  </div>
                </template>
              </vue-select>
            </div>

            <!-- QUANTITY -->
            <div class="col-md-3">
              <label class="form-label fw-semibold" style="font-size:11px;">Quantity <span class="text-danger">*</span></label>
              <input type="number" min="1" class="form-control" v-model.number="row.quantity" placeholder="Qty" />
              <div v-if="getRowError(index, 'quantity')" style="font-size:10.5px;color:#f43f5e;margin-top:3px;">
                {{ getRowError(index, 'quantity') }}
              </div>
            </div>

            <!-- DESCRIPTION -->
            <div class="col-12">
              <label class="form-label fw-semibold mt-2" style="font-size:11px;">Description</label>
              <textarea class="form-control" rows="2" v-model="row.description" placeholder="Optional description..."></textarea>
              <div v-if="getRowError(index, 'item_id')" style="font-size:10.5px;color:#f43f5e;margin-top:3px;">
                {{ getRowError(index, 'item_id') }}
              </div>
            </div>

            <div class="col-12 text-end mt-2" v-if="rows.length > 1">
              <button type="button" class="btn btn-sm btn-outline-danger" @click="removeRow(index)">
                <i class="ti ti-trash"></i> Remove
              </button>
            </div>

          </div>
        </div>

        <button type="button" class="btn btn-sm btn-outline-primary" @click="addRow">
          <i class="ti ti-plus"></i> Add Another Item
        </button>

      </div>

      <!-- Footer -->
      <div class="modal-footer" style="border-top:1px solid #e2e8f0;padding:11px 20px;gap:8px;">
        <!-- <button type="button" class="btn btn-light" data-bs-dismiss="modal" @click="resetForm">Cancel</button> -->
        <button type="button" class="btn btn-primary" @click="save">
          <i class="ti ti-device-floppy"></i> Save
        </button>
      </div>

    </div>
  </div>
</div>
</template>